// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import OrderActionSet, {
    orderActionSetFieldDefinitions,
    OrderActionSetPresentationProps,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import constants from "@store/constants";
import getOrderChangeRequestAvailability from "@store/Framework/Common/Utilities/OrderChangeRequest/getOrderChangeRequestAvailability";
import OrderChangeRequestProbabilityAlert from "@store/Library/Components/OrderChangeRequest/OrderChangeRequestProbabilityAlert";
import getOrderChangeRequestHasChanges from "@store/Framework/Common/Utilities/OrderChangeRequest/getOrderChangeRequestHasChanges";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

const enum fields {
    showProbabilityAlert = "showProbabilityAlert",
    showCancel = "showCancel",
    showReviewChanges = "showReviewChanges",
}

export interface OrderChangeRequestActionsProps extends WidgetProps {
    fields: {
        [fields.showProbabilityAlert]?: boolean;
        [fields.showCancel]?: boolean;
        [fields.showReviewChanges]?: boolean;
    };
}

export interface OrderChangeRequestActionsStyles {
    alertWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    cancelLink?: LinkPresentationProps;
    orderActionSet?: OrderActionSetPresentationProps;
    reviewChangesButton?: ButtonPresentationProps;
    reviewChangesLink?: LinkPresentationProps;
}

export const orderChangeRequestActionsStyles: OrderChangeRequestActionsStyles = {
    orderActionSet: {
        extendedStyles: {
            orderActions: {
                extendedStyles: {
                    flexWrapper: {
                        item: {
                            css: css`
                                &:has(> .alert) {
                                    padding: ${pxToRem(8, 0)};

                                    &:first-child {
                                        padding-top: 0;
                                    }

                                    &:last-child {
                                        padding-bottom: 0;
                                    }
                                }

                                &:has(> .alert:empty) {
                                    display: none;
                                }
                            `,
                        },
                    },
                },
            },
            orderStickyAction: {
                extendedStyles: {
                    flexWrapper: {
                        item: {
                            css: css`
                                &:has(> .alert) {
                                    flex-basis: 100%;
                                }

                                &:has(> .alert:empty) {
                                    display: none;
                                }
                            `,
                        },
                    },
                },
            },
        },
    },
    alertWrapper: {
        css: css`
            width: 100%;
        `,
    },
    reviewChangesButton: { variant: "primary" },
    cancelButton: { variant: "tertiary" },
};

const styles = orderChangeRequestActionsStyles;

const OrderChangeRequestActions: FC<OrderChangeRequestActionsProps> = ({ fields: widgetFields }) => {
    const history = useHistory();
    const { value: order } = useContext(OrderStateContext);
    const { orderLines, newLines, newPoNumber, newRequestedDate, newShipToCode } = useAppSelector(
        state => state.greif.pages.orderChange.changeRequestOrder,
    );
    const cancelPageLink = usePageLinkByPageType(constants.pageType.orderDetails);
    const reviewPageLink = usePageLinkByPageType(constants.pageType.orderChangeRequestReview);
    const { canRequestOrderChange } = getOrderChangeRequestAvailability(order);

    if (!order) {
        return null;
    }

    const canReviewChanges = () => {
        const allLines = [...(orderLines ?? []), ...(newLines ?? [])];
        const hasChanges = getOrderChangeRequestHasChanges({
            orderLines,
            newLines,
            newPoNumber,
            newRequestedDate,
            newShipToCode,
        });
        const hasLineErrors = allLines.some(x => x.hasError);

        return hasChanges && !hasLineErrors;
    };

    return (
        <OrderActionSet
            {...styles.orderActionSet}
            fields={widgetFields}
            actions={[
                {
                    button: (
                        <StyledWrapper
                            {...styles.alertWrapper}
                            className="alert"
                            data-test-selector="orderChangeRequestProbabilityAlert"
                        >
                            <OrderChangeRequestProbabilityAlert key="probabilityAlert" />
                        </StyledWrapper>
                    ),
                    field: fields.showProbabilityAlert,
                },
                {
                    key: "reviewChanges",
                    label: translate("Review Changes"),
                    onClick: () => {
                        if (reviewPageLink?.url && canReviewChanges() && canRequestOrderChange) {
                            const orderNumber = order.erpOrderNumber || order.webOrderNumber;
                            history.push(`${reviewPageLink.url}?orderNumber=${encodeURIComponent(orderNumber)}`);
                        }
                    },
                    buttonStyles: styles.reviewChangesButton,
                    linkStyles: styles.reviewChangesLink,
                    isDisabled: !reviewPageLink?.url || !canReviewChanges() || !canRequestOrderChange,
                    field: fields.showReviewChanges,
                    testSelector: "orderChangeRequestActions_reviewChanges",
                },
                {
                    key: "cancel",
                    label: translate("Cancel"),
                    href: cancelPageLink?.url
                        ? `${cancelPageLink.url}?orderNumber=${encodeURIComponent(
                              order.erpOrderNumber || order.webOrderNumber,
                          )}`
                        : undefined,
                    buttonStyles: styles.cancelButton,
                    linkStyles: styles.cancelLink,
                    isDisabled: !cancelPageLink?.url,
                    field: fields.showCancel,
                    testSelector: "orderChangeRequestActions_cancel",
                },
            ]}
        />
    );
};

const widgetModule: WidgetModule = {
    component: OrderChangeRequestActions,
    definition: {
        group: constants.widgetGroup.orderChangeRequest,
        displayName: "Order Change Request Actions",
        allowedContexts: [constants.pageType.orderChangeRequest],
        fieldDefinitions: [
            ...orderActionSetFieldDefinitions([
                { name: fields.showProbabilityAlert, defaultState: "sticky" },
                { name: fields.showReviewChanges, defaultState: "sticky" },
                { name: fields.showCancel, defaultState: "sticky" },
            ]),
        ],
    },
};

export default widgetModule;
